export class AnimationController {
    constructor() {
        this.transitionDuration = 600; // Snappy but smooth
        this.easings = {
            easeOutCubic: 'cubic-bezier(0.33, 1, 0.68, 1)',
            easeInCubic: 'cubic-bezier(0.32, 0, 0.67, 0)',
            easeInOutCubic: 'cubic-bezier(0.65, 0, 0.35, 1)'
        };
    }

    /**
     * Animate content appearing with staggered entrance
     */
    animateContentIn(container) {
        const elements = container.querySelectorAll('.question-number, .question-text, .question-description, .answer-container > *, .validation-error');
        
        elements.forEach((element, index) => {
            // Reset initial state
            element.style.opacity = '0';
            element.style.transform = 'translateY(20px)';
            element.style.transition = 'none';
            
            // Force reflow
            element.offsetHeight;
            
            // Apply transition and animate in
            element.style.transition = `opacity 0.4s ${this.easings.easeOutCubic} ${index * 0.1}s, transform 0.4s ${this.easings.easeOutCubic} ${index * 0.1}s`;
            
            // Trigger animation
            requestAnimationFrame(() => {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
            });
        });
    }

    /**
     * Handle slide transitions with camera movement effect
     */
    async slideTransition(direction = 'next', newContent = '') {
        const questionContainer = document.getElementById('question-container');
        const currentSlide = document.getElementById('current-slide');
        const nextSlide = document.getElementById('next-slide');
        
        // Set new content to next slide before animation
        nextSlide.innerHTML = newContent;
        
        return new Promise((resolve) => {
            // Position slides side by side for camera movement
            this.setupCameraMovement(currentSlide, nextSlide, direction);
            
            // Animate camera movement
            this.animateCameraMovement(questionContainer, direction);
            
            setTimeout(() => {
                // Complete the transition and reset
                this.completeCameraTransition(currentSlide, nextSlide, questionContainer);
                resolve();
            }, this.transitionDuration);
        });
    }
    
    /**
     * Setup slides for camera movement effect
     */
    setupCameraMovement(currentSlide, nextSlide, direction) {
        const containerWidth = document.getElementById('question-container').offsetWidth;
        
        // Remove hidden class from next slide
        nextSlide.classList.remove('hidden');
        
        // Position current slide at center (0)
        currentSlide.style.position = 'absolute';
        currentSlide.style.top = '0';
        currentSlide.style.left = '0';
        currentSlide.style.width = '100%';
        currentSlide.style.transform = 'translateX(0)';
        currentSlide.style.opacity = '1';
        
        // Position next slide to the right or left based on direction
        nextSlide.style.position = 'absolute';
        nextSlide.style.top = '0';
        nextSlide.style.left = '0';
        nextSlide.style.width = '100%';
        
        if (direction === 'next') {
            // Next slide starts to the right
            nextSlide.style.transform = `translateX(${containerWidth}px)`;
        } else {
            // Previous slide starts to the left  
            nextSlide.style.transform = `translateX(-${containerWidth}px)`;
        }
        nextSlide.style.opacity = '1';
    }
    
    /**
     * Animate camera movement between slides
     */
    animateCameraMovement(questionContainer, direction) {
        const currentSlide = document.getElementById('current-slide');
        const nextSlide = document.getElementById('next-slide');
        const containerWidth = questionContainer.offsetWidth;
        
        // Apply smooth transition
        const transition = `transform ${this.transitionDuration}ms ${this.easings.easeInOutCubic}`;
        currentSlide.style.transition = transition;
        nextSlide.style.transition = transition;
        
        // Move both slides to create camera movement effect
        requestAnimationFrame(() => {
            if (direction === 'next') {
                // Camera moves right: current slide moves left, next slide moves to center
                currentSlide.style.transform = `translateX(-${containerWidth}px)`;
                nextSlide.style.transform = 'translateX(0)';
            } else {
                // Camera moves left: current slide moves right, next slide moves to center
                currentSlide.style.transform = `translateX(${containerWidth}px)`;
                nextSlide.style.transform = 'translateX(0)';
            }
        });
    }
    
    /**
     * Complete camera transition and reset slide positions
     */
    completeCameraTransition(currentSlide, nextSlide, questionContainer) {
        // Clear transitions and reset styles
        currentSlide.style.transition = '';
        currentSlide.style.transform = '';
        currentSlide.style.position = '';
        currentSlide.style.top = '';
        currentSlide.style.left = '';
        currentSlide.style.width = '';
        currentSlide.style.opacity = '';
        currentSlide.innerHTML = '';
        currentSlide.classList.add('hidden');
        
        nextSlide.style.transition = '';
        nextSlide.style.transform = '';
        nextSlide.style.position = '';
        nextSlide.style.top = '';
        nextSlide.style.left = '';
        nextSlide.style.width = '';
        nextSlide.style.opacity = '';
        nextSlide.classList.remove('hidden');
        
        // Swap slide IDs
        nextSlide.id = 'current-slide';
        currentSlide.id = 'next-slide';
    }


    /**
     * Animate sub-questions appearing with parent question fading
     */
    revealSubQuestions(parentElement, subElements) {
        // Fade parent question slightly
        parentElement.style.transition = 'opacity 0.3s ease-out';
        parentElement.style.opacity = '0.6';
        
        // Animate sub-questions in with stagger
        subElements.forEach((element, index) => {
            element.style.opacity = '0';
            element.style.transform = 'translateY(15px)';
            element.style.transition = 'none';
            
            // Force reflow
            element.offsetHeight;
            
            element.style.transition = `opacity 0.4s ${this.easings.easeOutCubic} ${index * 0.1}s, transform 0.4s ${this.easings.easeOutCubic} ${index * 0.1}s`;
            
            requestAnimationFrame(() => {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
            });
        });
    }

    /**
     * Animate button/option selection
     */
    animateSelection(element) {
        element.style.transition = 'transform 0.15s ease-out, box-shadow 0.15s ease-out';
        element.style.transform = 'scale(0.95)';
        
        setTimeout(() => {
            element.style.transform = 'scale(1)';
        }, 150);
    }

    /**
     * Animate progress bar updates
     */
    updateProgress(progressBar, percentage) {
        progressBar.style.transition = `width 0.5s ${this.easings.easeOutCubic}`;
        progressBar.style.width = `${percentage}%`;
    }

    /**
     * Animate validation error appearance
     */
    showValidationError(errorElement) {
        errorElement.style.opacity = '0';
        errorElement.style.transform = 'translateY(-10px)';
        errorElement.style.transition = 'none';
        
        // Force reflow
        errorElement.offsetHeight;
        
        errorElement.style.transition = `opacity 0.3s ${this.easings.easeOutCubic}, transform 0.3s ${this.easings.easeOutCubic}`;
        
        requestAnimationFrame(() => {
            errorElement.style.opacity = '1';
            errorElement.style.transform = 'translateY(0)';
        });
        
        // Auto-fade out
        setTimeout(() => {
            errorElement.style.transition = `opacity 0.5s ${this.easings.easeInCubic}, transform 0.5s ${this.easings.easeInCubic}`;
            errorElement.style.opacity = '0';
            errorElement.style.transform = 'translateY(-10px)';
        }, 2500);
    }

    /**
     * Animate completion screen
     */
    showCompletion(container) {
        const elements = container.querySelectorAll('h1, p, button');
        
        elements.forEach((element, index) => {
            element.style.opacity = '0';
            element.style.transform = 'translateY(30px)';
            element.style.transition = 'none';
            
            // Force reflow
            element.offsetHeight;
            
            element.style.transition = `opacity 0.6s ${this.easings.easeOutCubic} ${index * 0.2}s, transform 0.6s ${this.easings.easeOutCubic} ${index * 0.2}s`;
            
            requestAnimationFrame(() => {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
            });
        });
    }

    /**
     * Animate hover effects
     */
    addHoverAnimations() {
        const style = document.createElement('style');
        style.textContent = `
            .scale-option:hover, .boolean-option:hover {
                transform: translateY(-2px);
                transition: transform 0.2s ${this.easings.easeOutCubic};
            }
            
            .nav-btn:hover:not(:disabled) {
                transform: translateY(-1px);
                transition: transform 0.2s ${this.easings.easeOutCubic};
            }
            
            .open-input:focus {
                transform: scale(1.02);
                transition: transform 0.2s ${this.easings.easeOutCubic};
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Initialize all animations
     */
    init() {
        this.addHoverAnimations();
        
        // Add smooth scrolling behavior
        document.documentElement.style.scrollBehavior = 'smooth';
    }
}